import { Megaphone, Menu, UserRound, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { useSession } from '@/api/auth';
import { useSite } from '@/api/content';
import { Button, Icon, IconButton, Logo } from '@/components';
import { whatsAppLink, DEFAULT_QUESTION_MESSAGE } from '@/lib/whatsapp';
import { NEUTRAL_COPY } from '@/lib/site';

import styles from './Shell.module.css';

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

function useOnline() {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function Shell() {
  const site = useSite();
  const session = useSession();
  const signedIn = Boolean(session.data);
  const scrolled = useScrolled();
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const lastPath = useRef(location.pathname);

  useEffect(() => {
    setMenuOpen(false);
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    // Move focus to the page on navigation so screen readers announce the new content.
    window.scrollTo(0, 0);
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  const config = site.data;
  const wa = whatsAppLink(config?.whatsapp.number, DEFAULT_QUESTION_MESSAGE);
  const navVariant = (active: boolean) => (active ? 'secondary' : 'ghost');

  return (
    <div className={styles.shell}>
      <a href="#main" className={styles.skip}>
        Skip to content
      </a>
      {!online && (
        <div className={styles.offline} role="status">
          You're offline. What you see may be out of date until you reconnect.
        </div>
      )}
      {config?.banner.on && (
        <div className={styles.banner} role="note">
          <div className={styles.bannerInner}>
            <Icon icon={Megaphone} size={16} />
            <span>{config.banner.text}</span>
          </div>
        </div>
      )}
      <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logoLink} aria-label="TripCircle, all trips">
            <Logo height={34} />
          </Link>
          <nav aria-label="Main" className={styles.nav}>
            <Button
              size="sm"
              variant={navVariant(
                location.pathname === '/' || location.pathname.startsWith('/trips'),
              )}
              to="/"
            >
              Trips
            </Button>
            <Button
              size="sm"
              variant={navVariant(location.pathname.startsWith('/past-trips'))}
              to="/past-trips"
            >
              Past trips
            </Button>
            {signedIn ? (
              <Button
                size="sm"
                iconLeft={UserRound}
                variant={navVariant(location.pathname.startsWith('/account'))}
                to="/account"
              >
                My trips
              </Button>
            ) : (
              <Button size="sm" variant="outline" to="/sign-in">
                Sign in
              </Button>
            )}
          </nav>
          <IconButton
            className={styles.menuButton}
            icon={menuOpen ? X : Menu}
            label={menuOpen ? 'Close menu' : 'Menu'}
            variant="outline"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
          />
        </div>
        <nav id="mobile-nav" aria-label="Main" className={styles.mobileNav} hidden={!menuOpen}>
          <Link to="/" className={styles.mobileLink}>
            Upcoming trips
          </Link>
          <Link to="/past-trips" className={styles.mobileLink}>
            Past trips
          </Link>
          <Link to="/find-booking" className={styles.mobileLink}>
            Find my booking
          </Link>
          {signedIn ? (
            <Link to="/account" className={styles.mobileLink}>
              My trips and details
            </Link>
          ) : (
            <div className={styles.mobileActions}>
              <Button variant="outline" fullWidth to="/sign-in">
                Sign in with your phone
              </Button>
            </div>
          )}
        </nav>
      </header>
      <main id="main" ref={mainRef} tabIndex={-1} className={styles.main}>
        <Outlet />
      </main>
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <p className={styles.footerCopy}>{config?.copy.footer ?? NEUTRAL_COPY.footer}</p>
          <ul className={styles.footerLinks}>
            {wa && (
              <li>
                <a href={wa} target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
              </li>
            )}
            {config?.social.instagram && (
              <li>
                <a href={config.social.instagram} target="_blank" rel="noopener noreferrer">
                  Instagram
                </a>
              </li>
            )}
            {config?.social.tiktok && (
              <li>
                <a href={config.social.tiktok} target="_blank" rel="noopener noreferrer">
                  TikTok
                </a>
              </li>
            )}
            <li>
              <Link to="/find-booking">Find my booking</Link>
            </li>
            <li>
              <Link to="/terms">Trip terms</Link>
            </li>
            <li>
              <Link to="/refunds">Refund policy</Link>
            </li>
            <li>
              <Link to="/privacy">Privacy</Link>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
}
